/**
 * Admin TG Activity Page
 * Shows TG activity log and statistics
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  ExternalLink,
  LogIn,
  Eye,
  Loader2
} from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import {
  getTGActivityLog,
  getTGActivitySummary,
  getPageNameLabel
} from '../../services/tgActivity'
import type { TGPageName, TGActionType } from '../../types/tgActivity'

// Color palette for TG cards
const TG_COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
  '#14B8A6', // teal
  '#A855F7', // purple
  '#22C55E', // green
  '#0EA5E9', // sky
  '#E11D48', // rose
]

function getColorForTG(index: number): string {
  return TG_COLORS[index % TG_COLORS.length]
}

export function AdminTGActivity() {
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<string | null>(null)
  const [selectedPage, setSelectedPage] = useState<TGPageName | null>(null)
  const [selectedActionType, setSelectedActionType] = useState<TGActionType | null>(null)
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [limit, setLimit] = useState<number>(50)
  const [hideInactive, setHideInactive] = useState<boolean>(true)
  const [assignmentNumbers, setAssignmentNumbers] = useState<Record<string, number | null>>({})

  const { data: summary = [], isLoading: summaryLoading } = useQuery({
    queryKey: ['tg-activity-summary'],
    queryFn: getTGActivitySummary
  })

  // Sort by activity (most active first) and optionally filter inactive
  const sortedSummary = [...summary]
    .sort((a, b) => b.total_visits - a.total_visits)
    .filter(s => !hideInactive || s.total_visits > 0)

  const { data: activityLog = [], isLoading: logLoading } = useQuery({
    queryKey: ['tg-activity-log', selectedSubcontractor, selectedPage, selectedActionType, dateFrom, dateTo, limit],
    queryFn: () => getTGActivityLog({
      subcontractorId: selectedSubcontractor || undefined,
      pageName: selectedPage || undefined,
      actionType: selectedActionType || undefined,
      startDate: dateFrom || undefined,
      endDate: dateTo ? `${dateTo}T23:59:59` : undefined,
      limit
    })
  })

  // Fetch assignment numbers for assignment_detail entries
  useEffect(() => {
    async function fetchAssignmentNumbers() {
      const assignmentIds = activityLog
        .filter(e => e.page_name === 'assignment_detail' && e.entity_id)
        .map(e => e.entity_id!)
        .filter((id, idx, arr) => arr.indexOf(id) === idx) // unique

      if (assignmentIds.length === 0) return

      // Only fetch IDs we don't already have
      const missingIds = assignmentIds.filter(id => !(id in assignmentNumbers))
      if (missingIds.length === 0) return

      const { data } = await supabase
        .from('assignments')
        .select('id, assignment_number')
        .in('id', missingIds)

      if (data) {
        const newNumbers: Record<string, number | null> = { ...assignmentNumbers }
        data.forEach(a => {
          newNumbers[a.id] = a.assignment_number
        })
        setAssignmentNumbers(newNumbers)
      }
    }
    fetchAssignmentNumbers()
  }, [activityLog])

  // Create a color map for subcontractors (based on original order for consistency)
  const colorMap = new Map<string, string>()
  summary.forEach((s, index) => {
    colorMap.set(s.subcontractor_id, getColorForTG(index))
  })

  const inactiveCount = summary.filter(s => s.total_visits === 0).length

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100 flex items-center space-x-3">
          <Activity className="h-7 w-7" />
          <span>Aktivitet</span>
        </h1>
        <p className="text-nature-600 dark:text-nature-400">
          Trädgårdsmästarnas aktivitet i systemet
        </p>
      </div>

      {/* TG Summary Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-nature-600 dark:text-nature-400">
          {sortedSummary.length} Trädgårdsmästare {hideInactive && inactiveCount > 0 && `(${inactiveCount} dolda utan aktivitet)`}
        </div>
        {inactiveCount > 0 && (
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="rounded border-nature-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-nature-600 dark:text-nature-400">
              Dölj trädgårdsmästare utan aktivitet ({inactiveCount})
            </span>
          </label>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedSummary.map((s) => {
          const tgColor = colorMap.get(s.subcontractor_id) || '#6B7280'
          const lastActive = s.last_activity || s.last_login
          return (
            <button
              key={s.subcontractor_id}
              onClick={() => setSelectedSubcontractor(
                selectedSubcontractor === s.subcontractor_id ? null : s.subcontractor_id
              )}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                selectedSubcontractor === s.subcontractor_id
                  ? 'ring-2 ring-offset-2 dark:ring-offset-nature-900'
                  : 'hover:shadow-md'
              } ${s.total_visits === 0 ? 'opacity-60' : ''}`}
              style={{
                borderColor: tgColor,
                backgroundColor: selectedSubcontractor === s.subcontractor_id ? `${tgColor}15` : undefined,
                ['--tw-ring-color' as string]: tgColor
              }}
            >
              <div className="flex items-center space-x-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: tgColor }}
                >
                  {s.subcontractor_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-nature-900 dark:text-nature-100 truncate">
                      {s.subcontractor_name}
                    </h4>
                    {!s.is_active && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-xs rounded-full font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                        Inaktiv
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-nature-500 dark:text-nature-400">
                    {s.total_visits} sidvisningar • {s.total_logins} inloggningar
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-nature-500 dark:text-nature-400">Senast aktiv:</span>
                  {lastActive ? (
                    <span className="font-medium text-nature-700 dark:text-nature-300">
                      {format(new Date(lastActive), 'yyyy-MM-dd HH:mm')}
                    </span>
                  ) : (
                    <span className="text-nature-400 italic">Aldrig</span>
                  )}
                </div>
                {lastActive && (
                  <p className="text-xs text-nature-400 mt-1">
                    {formatDistanceToNow(new Date(lastActive), { addSuffix: true, locale: sv })}
                  </p>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Filters */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">Typ</label>
            <select
              value={selectedActionType || ''}
              onChange={(e) => setSelectedActionType(e.target.value as TGActionType || null)}
              className="input-field w-auto text-sm py-1.5"
            >
              <option value="">Alla</option>
              <option value="page_view">Sidvisningar</option>
              <option value="login">Inloggningar</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">Sida</label>
            <select
              value={selectedPage || ''}
              onChange={(e) => setSelectedPage(e.target.value as TGPageName || null)}
              className="input-field w-auto text-sm py-1.5"
              disabled={selectedActionType === 'login'}
            >
              <option value="">Alla sidor</option>
              <option value="dashboard">Dashboard</option>
              <option value="calendar">Kalender</option>
              <option value="assignments">Mina Uppdrag</option>
              <option value="assignment_detail">Uppdragsdetalj</option>
              <option value="articles">Artiklar</option>
              <option value="handbook">Handbok</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">Från datum</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input-field w-auto text-sm py-1.5"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">Till datum</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input-field w-auto text-sm py-1.5"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">Antal</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="input-field w-auto text-sm py-1.5"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={10000}>Alla</option>
            </select>
          </div>
          {(selectedSubcontractor || selectedPage || selectedActionType || dateFrom || dateTo) && (
            <button
              onClick={() => {
                setSelectedSubcontractor(null)
                setSelectedPage(null)
                setSelectedActionType(null)
                setDateFrom('')
                setDateTo('')
              }}
              className="text-sm text-primary-600 hover:underline pb-1.5"
            >
              Rensa filter
            </button>
          )}
        </div>
        {selectedSubcontractor && (
          <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700">
            <span className="text-sm text-nature-600 dark:text-nature-400">
              Filtrerar på:{' '}
              <span className="font-medium text-nature-900 dark:text-nature-100">
                {summary.find(s => s.subcontractor_id === selectedSubcontractor)?.subcontractor_name}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Activity Log */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
          <h3 className="font-semibold text-nature-900 dark:text-nature-100">Aktivitetslogg</h3>
          <span className="text-sm text-nature-500 dark:text-nature-400">
            Visar {activityLog.length} poster{activityLog.length === limit && limit < 10000 && ` (max ${limit})`}
          </span>
        </div>
        {logLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-nature-400 mx-auto" />
          </div>
        ) : activityLog.length === 0 ? (
          <div className="p-8 text-center">
            <Activity className="h-12 w-12 text-nature-300 mx-auto mb-4" />
            <p className="text-nature-500">Ingen aktivitet hittad</p>
          </div>
        ) : (
          <div className="divide-y divide-nature-200 dark:divide-nature-700 max-h-96 overflow-y-auto">
            {activityLog.map(entry => {
              const tgColor = colorMap.get(entry.subcontractor_id) || '#6B7280'
              return (
                <div key={entry.id} className="px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                      style={{ backgroundColor: tgColor }}
                    >
                      {entry.subcontractor_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="font-medium text-nature-900 dark:text-nature-100">
                        {entry.subcontractor_name}
                      </span>
                      {summary.find(s => s.subcontractor_id === entry.subcontractor_id && !s.is_active) && (
                        <span className="px-1.5 py-0.5 text-xs rounded-full font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                          Inaktiv
                        </span>
                      )}
                      {entry.action_type === 'login' ? (
                        <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          <LogIn className="h-3 w-3" />
                          <span>Inloggning</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          <Eye className="h-3 w-3" />
                          <span>Sidvisning</span>
                        </span>
                      )}
                      {entry.page_name && (
                        <span className="text-nature-600 dark:text-nature-400 text-sm">
                          {getPageNameLabel(entry.page_name)}
                        </span>
                      )}
                      {entry.page_name === 'assignment_detail' && entry.entity_id && (
                        <Link
                          to={`/admin/assignments/${entry.entity_id}`}
                          className="inline-flex items-center space-x-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-sm font-medium">
                            #{assignmentNumbers[entry.entity_id] ?? '...'}
                          </span>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-nature-400 whitespace-nowrap ml-4">
                    {format(new Date(entry.created_at), 'yyyy-MM-dd HH:mm:ss')}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
